import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { throwError } from 'rxjs';
import { catchError, groupBy, map, mergeMap, throttleTime } from 'rxjs/operators';
import { CallTagsForSingleCallRemoteDto } from '../../../../generated/signalR/call-tags-for-single-call-remote-dto';
import { CallTagsService, SpecificMessageTagRequestData } from '../../../../generated/swagger';
import { ChatId } from '../chats/models/chat-id';
import {
   CallTagsUpdatedByServer,
   InitiateCallTagHistoryLoad,
   LoadCallTagForHistoryCall,
   LoadSpecificTagForHistoryMessage,
   SetMessageHistoryTag,
   SetUniqueCallIdForHistory,
   UpdateCallTag,
} from './call-tags.actions';

export class TagReplaceResult {
   constructor(public text: string, public allReplacedTagsFound: boolean) {}
}

export interface CallTagsStateModel {
   activeCalls: {
      calls: { [callId: number]: CallTagsForSingleCallRemoteDto };
   };
   historyCalls: {
      calls: { [uniqueCallId: string]: { [key: string]: string } };
   };
   historyMessages: {
      messages: { [messageId: string]: { [key: string]: string } };
   };
   lastSelectedHistoryId: string;
}

@State<CallTagsStateModel>({
   name: 'callTags',
   defaults: {
      activeCalls: {
         calls: {},
      },
      historyCalls: {
         calls: {},
      },
      historyMessages: {
         messages: {},
      },
      lastSelectedHistoryId: null,
   },
})
@Injectable()
export class CallTagsState {
   constructor(private callTagService: CallTagsService, private store: Store, private actions$: Actions) {
      this.actions$
         .pipe(
            ofActionSuccessful(InitiateCallTagHistoryLoad),
            groupBy((x: InitiateCallTagHistoryLoad) => x.uniqueCallId),
            mergeMap((group) =>
               group.pipe(
                  throttleTime(5000),
                  map((x) => this.store.dispatch(new LoadCallTagForHistoryCall(x.uniqueCallId)))
               )
            )
         )
         .subscribe();
   }

   @Selector([CallTagsState])
   public static lastSelectedHistoryId(state: CallTagsStateModel) {
      return state?.lastSelectedHistoryId;
   }

   @Selector([CallTagsState])
   public static callTagsForCalls(state: CallTagsStateModel) {
      return state?.activeCalls?.calls ?? {};
   }

   @Selector([CallTagsState])
   public static getCallTagsForHistoryCalls(state: CallTagsStateModel) {
      return state?.historyCalls?.calls ?? {};
   }

   @Selector([CallTagsState])
   public static historyMessages(state: CallTagsStateModel) {
      return state?.historyMessages?.messages ?? {};
   }

   @Selector([CallTagsState.callTagsForCalls])
   public static callTagIdsForCalls(callTags: { [callId: number]: CallTagsForSingleCallRemoteDto }) {
      return _(callTags).map((x) => x.callId);
   }

   @Selector([CallTagsState.callTagsForCalls])
   public static getCallTagsForCallId(callTags: { [callId: number]: CallTagsForSingleCallRemoteDto }): (callId: number) => { [key: string]: string } {
      return (callId: number) => {
         return callTags[callId]?.callTags ?? {};
      };
   }

   @Selector([CallTagsState.getCallTagsForHistoryCalls])
   public static getHistoryCallTagsForCall(historyCallTags: {
      [uniqueCallId: string]: { [key: string]: string };
   }): (uniqueCallId: string) => { [key: string]: string } {
      return (uniqueCallId: string) => {
         return historyCallTags[uniqueCallId] ?? {};
      };
   }

   @Selector([CallTagsState.lastSelectedHistoryId, CallTagsState.getHistoryCallTagsForCall])
   public static callTagsForSelectedHistoryCall(
      currentUniqueCallIdForHistoryCall: string,
      getHistoryCallTagsForCall: (uniqueCallId: string) => { [key: string]: string }
   ) {
      return getHistoryCallTagsForCall(currentUniqueCallIdForHistoryCall);
   }

   @Selector([CallTagsState.callTagsForCalls])
   public static allCallTags(callTags: { [callId: number]: CallTagsForSingleCallRemoteDto }): CallTagsForSingleCallRemoteDto[] {
      return _.values(callTags);
   }

   @Selector([CallTagsState.allCallTags])
   public static getAreMessageTagsAvailable(messageTags: CallTagsForSingleCallRemoteDto[]) {
      return (messageId: string) => {
         return messageTags.some((tag) => tag.callUId === messageId);
      };
   }

   @Selector([CallTagsState.allCallTags])
   public static getMessageTag(messageTags: CallTagsForSingleCallRemoteDto[]) {
      return (callId: number, messageId: string, tagName: string) => {
         return messageTags.find((tag) => tag.callId === callId || tag.callUId === messageId)?.callTags[tagName] ?? '';
      };
   }

   @Selector([CallTagsState.historyMessages])
   public static getHistoryMessageTags(messageTags: {
      [messageId: string]: {
         [key: string]: string;
      };
   }) {
      return (messageId: string) => {
         return messageTags[messageId] ?? {};
      };
   }

   @Selector([CallTagsState.getHistoryMessageTags])
   public static getIsHistoryMessageContainingTag(
      getHistoryMessageTags: (
         messageId: string
      ) => {
         [key: string]: string;
      }
   ) {
      return (messageId: string, tagName: string) => {
         return _.some(_.intersection([tagName], _.keys(getHistoryMessageTags(messageId))));
      };
   }

   @Selector([CallTagsState.getHistoryMessageTags, CallTagsState.getIsHistoryMessageContainingTag])
   public static getHistoryMessageTag(
      getHistoryMessageTags: (
         messageId: string
      ) => {
         [key: string]: string;
      },
      getIsHistoryMessageContainingTag: (messageId: string, tagName: string) => boolean
   ) {
      return (messageId: string, tagName: string) => {
         if (!getIsHistoryMessageContainingTag(messageId, tagName)) {
            return;
         }
         return getHistoryMessageTags(messageId)[tagName] ?? '';
      };
   }

   @Selector([CallTagsState.callTagsForCalls])
   public static getTagsForChat(callTags: { [callId: number]: CallTagsForSingleCallRemoteDto }): (chatId: ChatId) => { [key: string]: string } {
      return (chatId: ChatId) => {
         return callTags[chatId.callId].callTags;
      };
   }

   @Action(SetUniqueCallIdForHistory)
   public callHistoryEntrySelected(ctx: StateContext<CallTagsStateModel>, action: SetUniqueCallIdForHistory) {
      ctx.patchState({
         lastSelectedHistoryId: action.uniqueCallId,
      });

      return this.store.dispatch(new InitiateCallTagHistoryLoad(action.uniqueCallId));
   }

   @Action(UpdateCallTag)
   public updateCallTag(ctx: StateContext<CallTagsStateModel>, action: UpdateCallTag) {
      if (!_.isNil(action.messageId)) {
         return;
      }
      return this.callTagService.updateCallTag({
         callId: action.callId,
         callTagKey: action.tagKey,
         callTagValue: action.tagValue,
      });
   }

   @Action(UpdateCallTag)
   public updateCallTagForMessage(ctx: StateContext<CallTagsStateModel>, action: UpdateCallTag) {
      if (_.isNil(action.messageId)) {
         return;
      }
      return this.callTagService.updateCallTag({
         callId: action.callId,
         callTagKey: action.tagKey,
         callTagValue: action.tagValue,
         callUId: action.messageId,
      });
   }

   @Action(CallTagsUpdatedByServer)
   public callTagsUpdatedByServer(ctx: StateContext<CallTagsStateModel>, action: CallTagsUpdatedByServer) {
      const updatedCalls: { [callId: number]: CallTagsForSingleCallRemoteDto } = _.chain(action.calls)
         .keyBy((x) => x.callId)
         .mapValues((x) => x)
         .value();

      ctx.patchState({
         activeCalls: {
            calls: updatedCalls,
         },
      });
   }

   @Action(LoadCallTagForHistoryCall)
   @ImmutableContext()
   public loadCallTagForHistoryCall(ctx: StateContext<CallTagsStateModel>, action: LoadCallTagForHistoryCall) {
      if (_.isNil(action.uniqueCallId)) {
         return;
      }
      return this.callTagService.queryCallTagForCall(action.uniqueCallId).pipe(
         map((result) => {
            ctx.setState((state) => {
               state.historyCalls.calls[action.uniqueCallId] = result.callTags;
               return state;
            });
         })
      );
   }

   @Action(LoadSpecificTagForHistoryMessage)
   @ImmutableContext()
   public loadSpecificTagForHistoryMessage(ctx: StateContext<CallTagsStateModel>, action: LoadSpecificTagForHistoryMessage) {
      if (_.isNil(action.messageId) || _.isNil(action.tagName)) {
         return;
      }

      const requestData = <SpecificMessageTagRequestData>{
         messageId: action.messageId,
         tagName: action.tagName,
      };

      return this.callTagService.specificMessageTag(requestData).pipe(
         map((result) => ctx.dispatch(new SetMessageHistoryTag(action.messageId, action.tagName, result.tagForMessage))),
         catchError((error: HttpErrorResponse) => {
            console.error(`ResponseStatus for ${error.url} is ${error.statusText} (${error.status})`);
            return throwError(error);
         })
      );
   }

   @Action(SetMessageHistoryTag)
   @ImmutableContext()
   public setMessageHistoryTag(ctx: StateContext<CallTagsStateModel>, action: SetMessageHistoryTag) {
      const messageTag: { [key: string]: string } = {};
      messageTag[action.tagName] = action.tagValue;

      ctx.setState((state) => {
         state.historyMessages.messages[action.messageId] = messageTag;
         return state;
      });
   }
}
